import { computed, ref, watch } from 'vue';
import { AbstractAndReportExceptionAsync } from '@studyportals/mb-platform-exceptions-aop';
import { ResetEnrolmentsResult } from '@studyportals/sp-lord-business-interface';
import cookieHandler from '@/infrastructure/cookie-handler';
import lordBusiness from '@/infrastructure/lord-business-client';
import userCampaignManagementAPIClient from '@/infrastructure/user-campaign-management-api-client';
import store from '@/store';

const RESET_ACCESS_USER_IDS = [3032728, 301263];

interface ICachedResetUser {
	id: number | null;
	hasResetAccess: boolean;
}

class ResetEnrolmentsPresenter {
	public cachedUser = ref<ICachedResetUser>({ id: null, hasResetAccess: false });
	public isResetConfirmationShown = ref(false);
	private initialized = false;

	private selectedPSM = computed(() => store.state.selectedPSM);

	constructor() {
		watch(this.selectedPSM, () => this.onPsmSelected(), { immediate: true });
	}

	public get shouldShowResetButton(): boolean {
		return this.cachedUser.value.hasResetAccess && this.cachedUser.value.id !== null && RESET_ACCESS_USER_IDS.includes(this.cachedUser.value.id);
	}

	@AbstractAndReportExceptionAsync()
	public async resetEnrolments(enrolmentIdentities: string[]): Promise<ResetEnrolmentsResult | undefined> {
		return await lordBusiness.resetEnrolmentsByIds(enrolmentIdentities);
	}

	@AbstractAndReportExceptionAsync()
	private async onPsmSelected(): Promise<void> {
		if (!this.selectedPSM.value || this.selectedPSM.value === '0') {
			return;
		}

		if (this.initialized) {
			return;
		}


		const jwt = cookieHandler.retrieveTokenIdCookie();
		const email = this.getEmailFromJwt(jwt);
		if (!email) {
			return;
		}

		const userDto = await userCampaignManagementAPIClient.getUserByEmail(email, jwt);
		this.cachedUser.value.id = userDto.id;

		try {
			const hasAccess = await lordBusiness.validateResetEnrolmentsAccess();
			this.cachedUser.value.hasResetAccess = hasAccess;
		} catch {
			// Access validation is a special case for a limited set of users — silently ignore failures.
		}

		this.initialized = true;
	}

	private getEmailFromJwt(jwt: string): string {
		const parts = jwt.split('.');
		if (parts.length !== 3) {
			return '';
		}

		try {
			let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
			const paddingNeeded = (4 - (base64.length % 4)) % 4;
			if (paddingNeeded > 0) {
				base64 = base64 + '='.repeat(paddingNeeded);
			}
			const payload = JSON.parse(atob(base64)) as Record<string, unknown>;
			return typeof payload['cognito:username'] === 'string' ? payload['cognito:username'] : '';
		} catch {
			return '';
		}
	}
}

export default new ResetEnrolmentsPresenter();
